import React from 'react';
import { Typography, Button, CircularProgress, Box, useTheme } from '@mui/material';
import PendingIcon from '@mui/icons-material/Pending';

interface ResponseReviewProps {
  pendingResponses: (any | null)[];
  onSelect: (response: any) => void;
}

export default function ResponseReview({ pendingResponses, onSelect }: ResponseReviewProps) {
  const theme = useTheme();
  
  return (
    <Box sx={{ width: '100%', overflowX: 'auto', display: 'flex', alignItems: 'flex-start' }}>
      {pendingResponses.map((response, index) => (
        <Box 
          key={index} 
          sx={{ 
            minWidth: '800px', 
            maxWidth: '500px', 
            margin: '0 8px', 
            padding: '16px', 
            border: `1px solid ${theme.palette.divider}`, 
            borderRadius: '4px',
            backgroundColor: theme.palette.background.paper
          }}
        >
          <Typography variant="h6" gutterBottom>Response {index + 1}</Typography>
          <ResponseContent 
            response={response} 
            onSelect={onSelect}
          />
        </Box>
      ))}
    </Box>
  );
}

function ResponseContent({ response, onSelect }) {
  const theme = useTheme();
  
  if (response === null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100px' }}>
        <PendingIcon sx={{ marginRight: '8px' }} />
        <Typography>Pending...</Typography>
      </Box>
    );
  }

  if (response.error) {
    return <Typography color="error">{response.error}</Typography>;
  }

  const content = response.content || (response.data?.choices[0]?.message?.content ?? 'No content available');

  return (
    <Box sx={{ wordBreak: 'break-word' }}>
      <Typography 
        variant="body1" 
        sx={{ 
          padding: '8px', 
          borderRadius: '4px', 
          marginBottom: '16px',
          fontWeight: 'bold'
        }}
      >
        {content}
      </Typography>
      <Box
        component="pre"
        sx={{
          whiteSpace: 'pre-wrap',
          fontSize: '0.8rem',
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f8f8',
          color: theme.palette.text.primary,
          padding: '8px',
          borderRadius: '4px',
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'auto'
        }}
      >
        {JSON.stringify(response, null, 2)}
      </Box>
      <Button variant="contained" onClick={() => onSelect(response)} sx={{ marginTop: '16px' }}>
        Select
      </Button>
    </Box>
  );
}
